import React, { useState } from 'react';
import { useNavigate, useLocation, Link as RouterLink } from 'react-router-dom';
import {
  AppBar,
  Toolbar,
  IconButton,
  Chip,
  Button,
  Select,
  MenuItem,
  FormControl,
  Box,
  Menu,
  Tooltip,
  DialogTitle,
  DialogContent,
  Typography,
  TextField,
  CircularProgress,
  Alert,
} from '@mui/material';
import MenuIcon from '@mui/icons-material/Menu';
import MenuOpenIcon from '@mui/icons-material/MenuOpen';
import AccountCircle from '@mui/icons-material/AccountCircle';
import Logout from '@mui/icons-material/Logout';
import Language from '@mui/icons-material/Language';
import Settings from '@mui/icons-material/Settings';
import KeyboardArrowDown from '@mui/icons-material/KeyboardArrowDown';
import { useTranslation } from 'react-i18next';
import { useAuth } from '../../../contexts/AuthContext';
import { useSnackbar } from '../../../contexts/SnackbarContext';
import { sessionApi } from '../../../api/session.service';
import { l2Api } from '../../../api/l2.service';
import { getErrorMessage, isUnauthorized } from '../../../api/utils/error-handler';
import { TEXT_COLORS, BG_COLORS, BORDER_COLORS } from '../../../utils/colors';
import { DIALOG } from '../../../utils/responsive';
import { hideNumberSpinners } from '../../../theme/sxPresets';
import { GlassDialog, GlassButton, CurrencyIcon } from '../../ui';

// Common AppBar styles
export const appBarSx = {
  width: '100%',
  zIndex: (theme: any) => theme.zIndex.drawer + 1,
  background: 'transparent',
  backgroundColor: 'transparent',
  boxShadow: 'none',
  backdropFilter: 'none',
  border: 'none',
  borderBottom: 'none',
  '&::before': { display: 'none' },
  '&::after': { display: 'none' },
};

// Burger Menu Icon
interface BurgerMenuProps {
  onDrawerToggle?: () => void;
}

export const BurgerMenu: React.FC<BurgerMenuProps> = ({ onDrawerToggle }) => (
  <IconButton
    color="inherit"
    edge="start"
    onClick={onDrawerToggle}
    sx={{
      mr: 2,
      // inline-flex, а не block: у IconButton центрирование иконки держится на flex,
      // а с display:block иконка садится на текстовую базовую линию — под ней остаётся
      // место под свисающие буквы, и котлета выглядит на 3px выше остальных кнопок шапки
      display: 'inline-flex',
      '@media (min-width: 870px)': { display: 'none' },
    }}
  >
    <MenuIcon />
  </IconButton>
);

// Свернуть/развернуть сайдбар — только на десктопе
interface SidebarToggleProps {
  /** Сайдбар свёрнут. */
  collapsed?: boolean;
  /** Без обработчика кнопка не рисуется: раздел подключает сворачивание, когда оно ему нужно. */
  onToggle?: () => void;
}

/**
 * Кнопка «свернуть сайдбар». Стоит там же, где бургер, и на той же точке (870px) его подменяет:
 * ниже сайдбара нет, там шторка. Состояние раздел держит в `useSidebarCollapsed`.
 */
export const SidebarToggle: React.FC<SidebarToggleProps> = ({ collapsed = false, onToggle }) => {
  const { t } = useTranslation();
  if (!onToggle) return null;
  const label = t(collapsed ? 'common:sidebar.show' : 'common:sidebar.hide');
  return (
    <Tooltip title={label}>
      <IconButton
        color="inherit"
        edge="start"
        onClick={onToggle}
        aria-label={label}
        sx={{
          mr: 2,
          display: 'none',
          '@media (min-width: 870px)': { display: 'inline-flex' },
        }}
      >
        {collapsed ? <MenuIcon /> : <MenuOpenIcon />}
      </IconButton>
    </Tooltip>
  );
};

// Language Selector
interface LanguageSelectorProps {
  currentLocale: string;
  changingLocale: boolean;
  onLocaleChange: (newLocale: string) => void;
}

export const LanguageSelector: React.FC<LanguageSelectorProps> = ({
  currentLocale,
  changingLocale,
  onLocaleChange,
}) => (
  <FormControl
    sx={{
      minWidth: { xs: 50, sm: 60, md: 80 },
      '& .MuiOutlinedInput-root': {
        color: 'white',
        backgroundColor: 'transparent',
        '& fieldset': { borderColor: BORDER_COLORS.FOCUS },
        '&:hover fieldset': { borderColor: TEXT_COLORS.DISABLED },
        '&.Mui-focused fieldset': { borderColor: 'white' },
      },
      '& .MuiSelect-icon': { color: 'white' },
    }}
  >
    <Select
      value={currentLocale}
      onChange={(e) => onLocaleChange(e.target.value)}
      disabled={changingLocale}
      size="small"
      startAdornment={<Language sx={{ mr: { xs: 0, md: 1 }, fontSize: { xs: 18, md: 20 } }} />}
      MenuProps={{
        PaperProps: {
          sx: {
            backgroundColor: 'rgba(0, 0, 0, 0.4)',
            backdropFilter: 'blur(12px)',
            border: `1px solid ${BORDER_COLORS.LIGHTER}`,
          },
        },
      }}
    >
      <MenuItem value="en">EN</MenuItem>
      <MenuItem value="ru">RU</MenuItem>
      {/* value="uk" — код локали i18n (папка locales/uk, localStorage, бэкенд). Не менять на "ua".
          Отображаем "UA" как более привычное обозначение для пользователей */}
      <MenuItem value="uk">UA</MenuItem>
    </Select>
  </FormControl>
);

// User Info Chips (Money only - tokens moved to roulette page)
export const UserInfoChips: React.FC = () => {
  const { user, refreshUser } = useAuth();
  const { t } = useTranslation();
  const { showSuccess, showError } = useSnackbar();

  // Exchange dialog state
  const [exchangeOpen, setExchangeOpen] = useState(false);
  const [characterName, setCharacterName] = useState('');
  const [quantity, setQuantity] = useState('');
  const [submitting, setSubmitting] = useState(false);

  if (!user) return null;

  const chipSx = {
    background: BG_COLORS.GLASS_LIGHTER,
    border: `1px solid ${BORDER_COLORS.LIGHTER}`,
    color: 'white',
    fontWeight: 600,
    transition: 'all 0.3s ease',
    '& .MuiChip-icon': { color: 'white' },
    cursor: 'pointer',
  };

  // Keyframes for money chip animation
  const moneyChipAnimation = {
    '@keyframes goldPulse': {
      '0%, 100%': {
        boxShadow: '0 0 8px rgba(245, 158, 11, 0.3), inset 0 0 12px rgba(245, 158, 11, 0.05)',
        borderColor: 'rgba(245, 158, 11, 0.4)',
      },
      '50%': {
        boxShadow: '0 0 16px rgba(245, 158, 11, 0.5), inset 0 0 20px rgba(245, 158, 11, 0.1)',
        borderColor: 'rgba(245, 158, 11, 0.7)',
      },
    },
    '@keyframes iconShine': {
      '0%, 100%': {
        filter: 'brightness(1) drop-shadow(0 0 2px rgba(245, 158, 11, 0.3))',
      },
      '50%': {
        filter: 'brightness(1.3) drop-shadow(0 0 6px rgba(245, 158, 11, 0.6))',
      },
    },
    animation: 'goldPulse 3s ease-in-out infinite',
    '& .MuiChip-icon': {
      color: '#f59e0b',
      animation: 'iconShine 3s ease-in-out infinite',
    },
  };

  const handleOpenExchange = () => {
    setExchangeOpen(true);
    setCharacterName('');
    setQuantity('');
  };

  const handleCloseExchange = () => {
    if (submitting) return;
    setExchangeOpen(false);
    setCharacterName('');
    setQuantity('');
  };

  const handleQuantityChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const val = e.target.value;
    if (val === '') {
      setQuantity('');
      return;
    }
    // Block leading zeros
    if (val.length > 1 && val.startsWith('0')) return;
    const num = parseInt(val, 10);
    if (isNaN(num) || num < 0) return;
    if (num > 1000000) return;
    setQuantity(val);
  };

  const handleQuantityKeyDown = (e: React.KeyboardEvent) => {
    if (e.key === '-' || e.key === 'e' || e.key === 'E' || e.key === '+' || e.key === '.') {
      e.preventDefault();
    }
  };

  const quantityNum = quantity === '' ? 0 : parseInt(quantity, 10);
  const isFormValid = characterName.trim() !== '' && quantityNum >= 1 && quantityNum <= 1000000 && quantityNum <= (user.money || 0);

  const handleExchangeConfirm = async () => {
    if (!characterName.trim() || !isFormValid) return;

    try {
      setSubmitting(true);
      await l2Api.exchangeMoney(characterName.trim(), {
        quantity: quantityNum,
      });
      showSuccess(t('common:exchange.success'));
      await refreshUser();
      handleCloseExchange();
    } catch (err: unknown) {
      showError(getErrorMessage(err, t('common:exchange.error')));
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <>
      <Chip
        icon={<CurrencyIcon />}
        label={
          <Box component="span">
            <Box component="span" sx={{ display: { xs: 'inline' }, '@media (min-width: 1300px)': { display: 'none' } }}>
              {user.money || 0}
            </Box>
            <Box component="span" sx={{ display: 'none', '@media (min-width: 1300px)': { display: 'inline' } }}>
              {t('common:currency.coin', { count: user.money || 0 })}
            </Box>
          </Box>
        }
        onClick={handleOpenExchange}
        sx={{
          ...chipSx,
          ...moneyChipAnimation,
          '&:hover': {
            transform: 'translateY(-2px)',
            boxShadow: '0 0 20px rgba(245, 158, 11, 0.6), inset 0 0 24px rgba(245, 158, 11, 0.15)',
            borderColor: '#f59e0b',
            background: BG_COLORS.HOVER,
            animation: 'none',
            '& .MuiChip-icon': {
              animation: 'none',
              filter: 'brightness(1.4) drop-shadow(0 0 8px rgba(245, 158, 11, 0.8))',
            },
          },
        }}
      />

      <GlassDialog
        open={exchangeOpen}
        onClose={handleCloseExchange}
        maxWidth="sm"
        fullWidth
      >
        <DialogTitle sx={{ pb: 1, px: DIALOG.padding }}>
          <Typography fontWeight={700} sx={{ color: '#ffffff', fontSize: DIALOG.titleFontSize }}>
            {t('common:exchange.title')}
          </Typography>
          <Typography sx={{ color: TEXT_COLORS.SECONDARY, mt: 0.5, fontSize: DIALOG.descriptionFontSize }}>
            {t('common:exchange.description')}
          </Typography>
        </DialogTitle>
        <DialogContent sx={{ pt: DIALOG.padding, px: DIALOG.padding }}>
          <Alert
            severity="info"
            sx={{
              mb: { xs: 1, sm: 2 },
              mt: { xs: 0.5, sm: 1 },
              py: { xs: 0.5, sm: 1 },
              fontSize: { xs: '0.75rem', sm: '0.875rem' },
              backgroundColor: 'rgba(59, 130, 246, 0.1)',
              border: '1px solid rgba(59, 130, 246, 0.3)',
              color: TEXT_COLORS.PRIMARY,
              '& .MuiAlert-icon': {
                color: '#60a5fa',
              },
            }}
          >
            {t('common:exchange.lCoinWarning')}
          </Alert>
          <Box sx={{ display: 'flex', flexDirection: 'column', gap: { xs: 1, sm: 2 } }}>
            {/* Character name input */}
            <TextField
              fullWidth
              label={t('common:exchange.characterName')}
              value={characterName}
              onChange={(e) => setCharacterName(e.target.value)}
              inputProps={{ maxLength: 16 }}
              sx={{
                '& .MuiInputLabel-root': { fontSize: DIALOG.contentFontSize },
                '& .MuiInputBase-input': { fontSize: DIALOG.contentFontSize },
              }}
            />

            {/* Quantity input */}
            <TextField
              fullWidth
              type="number"
              label={t('common:exchange.quantity')}
              value={quantity}
              onChange={handleQuantityChange}
              onKeyDown={handleQuantityKeyDown}
              inputProps={{ min: 1, max: 1000000 }}
              error={quantityNum > (user.money || 0)}
              helperText={quantityNum > (user.money || 0) ? t('common:validation.maxValue', { max: user.money || 0 }) : ''}
              sx={{
                '& .MuiInputLabel-root': { fontSize: DIALOG.contentFontSize },
                '& .MuiInputBase-input': { fontSize: DIALOG.contentFontSize },
                '& .MuiFormHelperText-root': { fontSize: DIALOG.helperTextFontSize },
                ...hideNumberSpinners,
              }}
            />
          </Box>
        </DialogContent>
        <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1, px: DIALOG.padding, pb: DIALOG.padding, pt: 1 }}>
          <GlassButton
            onClick={handleExchangeConfirm}
            disabled={!isFormValid || submitting}
            sx={{ height: 36, fontSize: { xs: '0.75rem', sm: '0.875rem' } }}
          >
            {submitting ? <CircularProgress size={20} sx={{ mr: 1 }} /> : null}
            {t('common:exchange.confirm')}
          </GlassButton>
          <Button
            onClick={handleCloseExchange}
            disabled={submitting}
            sx={{
              height: 36,
              fontSize: { xs: '0.75rem', sm: '0.875rem' },
              color: TEXT_COLORS.MUTED,
              '&:hover': { backgroundColor: BG_COLORS.GLASS_LIGHT },
            }}
          >
            {t('common:buttons.cancel')}
          </Button>
        </Box>
      </GlassDialog>
    </>
  );
};

// User Menu with dropdown (Settings + Logout)
export const UserMenu: React.FC = () => {
  const navigate = useNavigate();
  const { user, logout } = useAuth();
  const { t } = useTranslation();
  const [anchorEl, setAnchorEl] = React.useState<null | HTMLElement>(null);
  const open = Boolean(anchorEl);

  const handleClick = (event: React.MouseEvent<HTMLElement>) => {
    setAnchorEl(event.currentTarget);
  };

  const handleClose = () => {
    setAnchorEl(null);
  };

  const handleSettings = () => {
    handleClose();
    navigate('/dashboard/settings');
  };

  const handleLogout = async () => {
    handleClose();
    try {
      await sessionApi.logoutCurrent();
      logout();
      navigate('/login');
    } catch (error: unknown) {
      if (isUnauthorized(error)) {
        logout();
        navigate('/login');
      } else {
        console.error('Failed to logout:', error);
        alert(getErrorMessage(error, 'Failed to logout. Please try again.'));
      }
    }
  };

  if (!user) return null;

  return (
    <>
      <Button
        color="inherit"
        onClick={handleClick}
        size="small"
        endIcon={<KeyboardArrowDown sx={{
          transition: 'transform 0.2s',
          transform: open ? 'rotate(180deg)' : 'rotate(0deg)',
        }} />}
        sx={{
          minWidth: '36px',
          px: 1,
          fontSize: '14px',
          textTransform: 'none',
          '&:hover': { background: BG_COLORS.GLASS_LIGHT },
          '@media (min-width: 1300px)': { minWidth: 'auto', px: 2 },
        }}
      >
        <AccountCircle sx={{ mr: 1, fontSize: 22 }} />
        <Box component="span" sx={{ display: 'none', '@media (min-width: 1300px)': { display: 'inline' } }}>
          {user.username}
        </Box>
      </Button>
      <Menu
        anchorEl={anchorEl}
        open={open}
        onClose={handleClose}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'right' }}
        transformOrigin={{ vertical: 'top', horizontal: 'right' }}
        PaperProps={{
          sx: {
            mt: 1,
            minWidth: 180,
            background: BG_COLORS.GLASS,
            backdropFilter: 'blur(12px)',
            border: `1px solid ${BORDER_COLORS.LIGHT}`,
            '& .MuiMenuItem-root': {
              px: 2,
              py: 1.5,
              gap: 1.5,
              '&:hover': {
                backgroundColor: BG_COLORS.GLASS_LIGHT,
              },
            },
          },
        }}
      >
        <MenuItem onClick={handleSettings}>
          <Settings sx={{ fontSize: 20 }} />
          {t('navigation:menu.settings')}
        </MenuItem>
        <MenuItem onClick={handleLogout} sx={{ color: '#ef4444' }}>
          <Logout sx={{ fontSize: 20 }} />
          {t('navigation:toolbar.logout')}
        </MenuItem>
      </Menu>
    </>
  );
};

// Navigation Button with responsive icon/text and tooltip
// Supports both internal routes (to) and external links (href)
interface NavButtonProps {
  icon: React.ReactNode;
  label: string;
  to?: string;           // Internal route (react-router)
  href?: string;         // External URL
  onClick?: () => void;  // Fallback for custom actions
  breakpoint?: number;
}

export const NavButton: React.FC<NavButtonProps> = ({
  icon,
  label,
  to,
  href,
  onClick,
  breakpoint = 2100
}) => {
  const location = useLocation();
  const [showTooltip, setShowTooltip] = React.useState(false);
  const [isCompact, setIsCompact] = React.useState(window.innerWidth < breakpoint);

  React.useEffect(() => {
    let timeoutId: ReturnType<typeof setTimeout>;
    const handleResize = () => {
      clearTimeout(timeoutId);
      timeoutId = setTimeout(() => {
        setIsCompact(window.innerWidth < breakpoint);
      }, 150);
    };
    window.addEventListener('resize', handleResize);
    return () => {
      clearTimeout(timeoutId);
      window.removeEventListener('resize', handleResize);
    };
  }, [breakpoint]);

  // Full reload only when already on this page/subpage, otherwise normal navigation
  const handleInternalClick = (e: React.MouseEvent) => {
    if (to) {
      const isOnSamePage = location.pathname === to || location.pathname.startsWith(to + '/');
      if (isOnSamePage) {
        e.preventDefault();
        window.location.href = to;
      }
      // Otherwise let RouterLink handle navigation normally
    }
  };

  // Determine button props based on link type
  const buttonProps: any = {
    color: 'inherit',
    size: 'small',
    sx: {
      minWidth: '40px',
      px: 1,
      fontSize: '14px',
      '&:hover': { background: BG_COLORS.GLASS_LIGHT },
      [`@media (min-width: ${breakpoint}px)`]: { minWidth: 'auto', px: 2 },
    },
  };

  if (to) {
    // Internal route - use react-router Link with click handler
    buttonProps.component = RouterLink;
    buttonProps.to = to;
    buttonProps.onClick = handleInternalClick;
  } else if (href) {
    // External link - use anchor tag
    buttonProps.component = 'a';
    buttonProps.href = href;
    buttonProps.target = '_blank';
    buttonProps.rel = 'noopener noreferrer';
  } else if (onClick) {
    // Fallback to onClick
    buttonProps.onClick = onClick;
  }

  return (
    <Tooltip
      title={label}
      open={showTooltip && isCompact}
      onOpen={() => setShowTooltip(true)}
      onClose={() => setShowTooltip(false)}
      placement="bottom"
    >
      <Button {...buttonProps}>
        <Box sx={{ mr: 0, display: 'flex', [`@media (min-width: ${breakpoint}px)`]: { mr: 1 } }}>
          {icon}
        </Box>
        <Box component="span" sx={{ display: 'none', [`@media (min-width: ${breakpoint}px)`]: { display: 'inline' } }}>
          {label}
        </Box>
      </Button>
    </Tooltip>
  );
};

// Export combined StyledAppBar
export { AppBar, Toolbar, Box };
